// Client for admin payment settings + mapping to UI gateways
import type { PaymentGateway } from '../types';

export type PaymentSettings = any;

export async function getPaymentSettings(): Promise<{ paymentSettings: PaymentSettings }>{
  const token = typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : null;
  const res = await fetch('/api/admin/payment-settings', { headers: { 'Accept': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
  if (!res.ok) throw new Error('Failed to load payment settings');
  return res.json();
}

export async function updatePaymentSettings(paymentSettings: PaymentSettings): Promise<{ paymentSettings: PaymentSettings }>{
  const token = typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : null;
  const res = await fetch('/api/admin/payment-settings', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ paymentSettings }) });
  if (!res.ok) throw new Error('Failed to save payment settings');
  return res.json();
}

// Map raw server paymentSettings into UI PaymentGateway[]
export function paymentSettingsToGateways(ps: PaymentSettings): PaymentGateway[] {
  const out: PaymentGateway[] = [];
  const stripe = ps?.stripe || {};
  if (stripe.enabled || stripe.publishableKey || stripe.secretKey) {
    // Core Stripe credentials
    out.push({
      id: 'stripe',
      name: 'Stripe',
      type: 'stripe',
      isActive: !!stripe.enabled,
      config: {
        publishableKey: stripe.publishableKey || '',
        secretKey: stripe.secretKey || '',
        webhookSecret: stripe.webhookSecret || '',
      },
    });
    // Card method toggle
    out.push({
      id: 'visa',
      name: 'Visa Card',
      type: 'visa',
      isActive: stripe.cardEnabled !== false, // default true when stripe enabled
      config: {},
    });
    // Google Pay toggle
    out.push({
      id: 'google_pay',
      name: 'Google Pay',
      type: 'google_pay',
      isActive: !!stripe.googlePayEnabled,
      config: {},
    });
  }
  const mpesa = ps?.mpesa || {};
  if (mpesa && (mpesa.enabled || mpesa.consumerKey || mpesa.shortCode)) {
    out.push({
      id: 'mpesa',
      name: 'M-Pesa',
      type: 'mpesa',
      isActive: !!mpesa.enabled,
      config: {
        apiKey: mpesa.consumerKey || '',
        secretKey: mpesa.consumerSecret || '',
        merchantId: mpesa.shortCode || '',
        passkey: mpesa.passkey || '',
      }
    });
  }
  const tigopesa = ps?.tigopesa || {};
  if (tigopesa && (tigopesa.enabled || tigopesa.clientId || tigopesa.merchantId)) {
    out.push({
      id: 'tigopesa',
      name: 'Tigo Pesa',
      type: 'tigopesa',
      isActive: !!tigopesa.enabled,
      config: {
        // reuse generic keys in UI
        apiKey: tigopesa.clientId || '',
        secretKey: tigopesa.clientSecret || '',
        merchantId: tigopesa.merchantId || '',
        // keep provider URLs if present
        tokenUrl: (tigopesa as any).tokenUrl || '',
        chargeUrl: (tigopesa as any).chargeUrl || '',
        callbackBaseUrl: (tigopesa as any).callbackBaseUrl || '',
      } as any,
    } as any);
  }
  const airtel = ps?.airtel || {};
  if (airtel && (airtel.enabled || airtel.clientId || airtel.merchantId)) {
    out.push({
      id: 'airtel_money',
      name: 'Airtel Money',
      type: 'airtel_money',
      isActive: !!airtel.enabled,
      config: {
        apiKey: airtel.clientId || '',
        secretKey: airtel.clientSecret || '',
        merchantId: airtel.merchantId || '',
        tokenUrl: (airtel as any).tokenUrl || '',
        chargeUrl: (airtel as any).chargeUrl || '',
        callbackBaseUrl: (airtel as any).callbackBaseUrl || '',
        country: (airtel as any).country || '',
        currency: (airtel as any).currency || '',
      } as any,
    } as any);
  }
  const pesapal = ps?.pesapal || {};
  if (pesapal && (pesapal.enabled || pesapal.consumerKey || pesapal.consumerSecret)) {
    out.push({
      id: 'pesapal',
      name: 'PesaPal',
      type: 'pesapal',
      isActive: !!pesapal.enabled,
      config: {
        consumerKey: pesapal.consumerKey || '',
        consumerSecret: pesapal.consumerSecret || '',
        environment: pesapal.environment || 'sandbox',
        callbackBaseUrl: pesapal.callbackBaseUrl || '',
        ipnId: pesapal.ipnId || '',
      } as any,
    } as any);
  }
  // Ensure consistent order like mockData fallback if empty
  if (!out.length) return [];
  return out;
}

// Merge UI PaymentGateway[] back into a paymentSettings object, preserving unknown fields
export function mergeGatewaysIntoPaymentSettings(gateways: PaymentGateway[], base: PaymentSettings = {}): PaymentSettings {
  const ps = JSON.parse(JSON.stringify(base || {})); // deep clone to avoid mutating caller
  (gateways || []).forEach((g) => {
    const type = String(g.type || '').toLowerCase();
    const cfg: any = g.config || {};
    if (type === 'stripe') {
      ps.stripe = ps.stripe || {};
      ps.stripe.enabled = g.isActive !== false;
      if (typeof cfg.publishableKey === 'string') ps.stripe.publishableKey = cfg.publishableKey;
      if (typeof cfg.secretKey === 'string') ps.stripe.secretKey = cfg.secretKey;
      if (typeof cfg.webhookSecret === 'string') ps.stripe.webhookSecret = cfg.webhookSecret;
    } else if (type === 'visa') {
      ps.stripe = ps.stripe || {};
      ps.stripe.cardEnabled = g.isActive !== false;
      if (ps.stripe.cardEnabled) ps.stripe.enabled = true;
    } else if (type === 'google_pay') {
      ps.stripe = ps.stripe || {};
      ps.stripe.googlePayEnabled = g.isActive !== false;
      if (ps.stripe.googlePayEnabled) ps.stripe.enabled = true;
    } else if (type === 'mpesa') {
      ps.mpesa = ps.mpesa || {};
      ps.mpesa.enabled = g.isActive !== false;
      if (typeof cfg.apiKey === 'string') ps.mpesa.consumerKey = cfg.apiKey;
      if (typeof cfg.secretKey === 'string') ps.mpesa.consumerSecret = cfg.secretKey;
      if (typeof cfg.merchantId === 'string') ps.mpesa.shortCode = cfg.merchantId;
      if (typeof cfg.passkey === 'string') ps.mpesa.passkey = cfg.passkey;
    } else if (type === 'tigopesa') {
      ps.tigopesa = ps.tigopesa || {};
      ps.tigopesa.enabled = g.isActive !== false;
      if (typeof cfg.apiKey === 'string') ps.tigopesa.clientId = cfg.apiKey;
      if (typeof cfg.clientId === 'string') ps.tigopesa.clientId = cfg.clientId;
      if (typeof cfg.secretKey === 'string') ps.tigopesa.clientSecret = cfg.secretKey;
      if (typeof cfg.clientSecret === 'string') ps.tigopesa.clientSecret = cfg.clientSecret;
      if (typeof cfg.merchantId === 'string') ps.tigopesa.merchantId = cfg.merchantId;
      if (typeof cfg.tokenUrl === 'string') (ps.tigopesa as any).tokenUrl = cfg.tokenUrl;
      if (typeof cfg.chargeUrl === 'string') (ps.tigopesa as any).chargeUrl = cfg.chargeUrl;
      if (typeof cfg.callbackBaseUrl === 'string') (ps.tigopesa as any).callbackBaseUrl = cfg.callbackBaseUrl;
    } else if (type === 'airtel_money') {
      ps.airtel = ps.airtel || {};
      ps.airtel.enabled = g.isActive !== false;
      if (typeof cfg.apiKey === 'string') ps.airtel.clientId = cfg.apiKey;
      if (typeof cfg.clientId === 'string') ps.airtel.clientId = cfg.clientId;
      if (typeof cfg.secretKey === 'string') ps.airtel.clientSecret = cfg.secretKey;
      if (typeof cfg.clientSecret === 'string') ps.airtel.clientSecret = cfg.clientSecret;
      if (typeof cfg.merchantId === 'string') ps.airtel.merchantId = cfg.merchantId;
      if (typeof cfg.tokenUrl === 'string') (ps.airtel as any).tokenUrl = cfg.tokenUrl;
      if (typeof cfg.chargeUrl === 'string') (ps.airtel as any).chargeUrl = cfg.chargeUrl;
      if (typeof cfg.callbackBaseUrl === 'string') (ps.airtel as any).callbackBaseUrl = cfg.callbackBaseUrl;
      if (typeof cfg.country === 'string') (ps.airtel as any).country = cfg.country;
      if (typeof cfg.currency === 'string') (ps.airtel as any).currency = cfg.currency;
    } else if (type === 'pesapal') {
      ps.pesapal = ps.pesapal || {};
      ps.pesapal.enabled = g.isActive !== false;
      if (typeof cfg.consumerKey === 'string') ps.pesapal.consumerKey = cfg.consumerKey;
      if (typeof cfg.apiKey === 'string' && !cfg.consumerKey) ps.pesapal.consumerKey = cfg.apiKey;
      if (typeof cfg.consumerSecret === 'string') ps.pesapal.consumerSecret = cfg.consumerSecret;
      if (typeof cfg.secretKey === 'string' && !cfg.consumerSecret) ps.pesapal.consumerSecret = cfg.secretKey;
      if (typeof cfg.environment === 'string') ps.pesapal.environment = cfg.environment;
      if (typeof cfg.callbackBaseUrl === 'string') ps.pesapal.callbackBaseUrl = cfg.callbackBaseUrl;
      if (typeof cfg.ipnId === 'string') ps.pesapal.ipnId = cfg.ipnId;
    }
  });
  return ps;
}
